import { useState } from 'react';

import { useAuth } from '../../providers/auth-provider';
import { useUsers } from '../../hooks/use-users';
import { useRoles } from '../../hooks/use-roles';
import { Avatar, colorForSeed, initialsFor } from '../conversations/Avatar';
import type { TenantUserResponse } from '../../lib/api-client';

import { InviteUserModal } from './InviteUserModal';
import { UserDetailPopover } from './UserDetailPopover';

function displayName(u: TenantUserResponse): string {
  return u.display_name ?? u.email.split('@')[0] ?? u.email;
}

export function TeamView() {
  const { user: currentUser } = useAuth();
  const { users, status, error, filters, setFilters, createUser, updateUser, deactivateUser } = useUsers();
  const { roles } = useRoles();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<TenantUserResponse | null>(null);

  // Gate on capabilities (resolved from the user's role), never role names.
  const canManage = currentUser?.capabilities?.manage_users === true || currentUser?.capabilities?.full_access === true;

  return (
    <div
      style={{
        padding: '20px 20px 24px',
        height: '100%',
        overflowY: 'auto',
        background: 'var(--bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Team</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {users.length} {users.length === 1 ? 'member' : 'members'}
        </div>

        <input
          placeholder="Filter by name or email…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{
            marginLeft: 'auto',
            padding: '5px 10px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'inherit',
            minWidth: 220,
          }}
        />
        <select
          value={filters.roleId ?? ''}
          onChange={(e) => setFilters({ ...filters, roleId: e.target.value || null })}
          style={selectStyle}
        >
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value as 'active' | 'deactivated' | 'all' })}
          style={selectStyle}
        >
          <option value="active">Active</option>
          <option value="deactivated">Deactivated</option>
          <option value="all">All</option>
        </select>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-soft)', cursor: 'pointer' }}
          title="Include users from tenants beneath this one"
        >
          <input
            type="checkbox"
            checked={filters.includeDescendants ?? false}
            onChange={(e) => setFilters({ ...filters, includeDescendants: e.target.checked })}
          />
          Include sub-tenants
        </label>
        {canManage && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            style={{
              padding: '6px 12px',
              background: 'var(--accent)',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            + Invite
          </button>
        )}
      </div>

      {status === 'loading' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>
      )}
      {status === 'error' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load team.'}
        </div>
      )}
      {status === 'success' && users.length === 0 && (
        <div style={{ padding: 24, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
          {filters.search || filters.roleId ? 'No members match those filters.' : 'No team members yet.'}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 10,
        }}
      >
        {users.map((u) => {
          const name = displayName(u);
          const color = colorForSeed(u.id);
          const isMe = u.id === currentUser?.id;
          return (
            <div
              key={u.id}
              onClick={() => setDetailUser(u)}
              style={{
                background: 'var(--surface)',
                border: `1px solid ${isMe ? 'var(--accent-soft)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
                transition: 'border-color 120ms ease',
              }}
            >
              <Avatar initials={initialsFor(name)} color={color} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name}
                  {isMe && (
                    <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6, fontWeight: 500 }}>
                      you
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-soft)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.email}
                </div>
              </div>
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--text-soft)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  background: 'var(--bg-alt)',
                  padding: '3px 8px',
                  borderRadius: 999,
                  fontWeight: 500,
                  flexShrink: 0,
                }}
              >
                {u.role_slug ?? '—'}
              </span>
            </div>
          );
        })}
      </div>

      {inviteOpen && (
        <InviteUserModal
          roles={roles}
          actorCapabilities={currentUser?.capabilities ?? {}}
          onCancel={() => setInviteOpen(false)}
          onSubmit={async (body) => {
            await createUser(body);
            setInviteOpen(false);
          }}
        />
      )}

      {detailUser && (
        <UserDetailPopover
          user={detailUser}
          roles={roles}
          canManage={canManage}
          actorCapabilities={currentUser?.capabilities ?? {}}
          isSelf={detailUser.id === currentUser?.id}
          onClose={() => setDetailUser(null)}
          onUpdate={async (body) => {
            const updated = await updateUser(detailUser.id, body);
            setDetailUser(updated);
          }}
          onDeactivate={async () => {
            await deactivateUser(detailUser.id);
            setDetailUser(null);
          }}
        />
      )}
    </div>
  );
}

const selectStyle = {
  padding: '5px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
} satisfies React.CSSProperties;
